import { useRef, useState, useContext, useCallback } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { alpha } from '@mui/material/styles';

import {
  AccountCircleOutlined as AccountsIcon,
  LoginOutlined as LoginIcon,
  SearchOutlined as SearchIcon,
  Brightness4Outlined as ToggleDarkModeIcon,
  Brightness5Outlined as ToggleLightModeIcon,
  Menu as MenuIcon,
  LocalMallTwoTone as LocalMallTwoToneIcon,
} from '@mui/icons-material';
import ProfilePopover from './ProfilePopover';
import CartPopover from './CartPopover';
import SearchBar from './SearchBar';
import SettingsContext from '../../contexts/SettingsContext';
import CartContext from '../../contexts/CartContext';
import { AuthContext } from '../../contexts/AuthContext';
import logo from '../../assets/logo.png';
import logoDark from '../../assets/logo_dark.png';
import { useNavigate } from 'react-router-dom';
import { ROOT_URL } from '../../constants/routerUrl';

const GrowBox = styled(Box)({
  flexGrow: 1,
});

const LogoImage = styled('img')(({ theme }) => ({
  display: 'block',
  height: theme.spacing(8),
  padding: theme.spacing(0, 1, 0, 0),
  [theme.breakpoints.down('sm')]: {
    height: theme.spacing(6),
  },
}));

const MobileSearchContainer = styled(Box)(({ theme, expanded }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.97),
  backdropFilter: 'blur(2px)',
  zIndex: 1300,
  opacity: expanded ? 1 : 0,
  transform: expanded ? 'scaleX(1)' : 'scaleX(0)',
  transformOrigin: 'center',
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeInOut,
  }),
  pointerEvents: expanded ? 'auto' : 'none',
}));

const Appbar = () => {
  const menuId = 'primary-search-account-menu';
  const theme = useTheme();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });
  const [isProfilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [isCartPopoverOpen, setCartPopoverOpen] = useState(false);
  const [isMobileSearchExpanded, setMobileSearchExpanded] = useState(false);
  const profileMenuRef = useRef();
  const cartRef = useRef();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useContext(SettingsContext);
  const cart = useContext(CartContext);
  const auth = useContext(AuthContext);

  const handleMobileSearchToggle = useCallback((event) => {
    event.stopPropagation();
    setMobileSearchExpanded(true);
  }, []);

  const handleMobileSearchClose = useCallback(() => {
    setMobileSearchExpanded(false);
  }, []);

  // Handle click on overlay background
  const handleOverlayClick = useCallback((event) => {
    // Only close if clicking on the overlay background itself, not on the search bar
    if (event.target === event.currentTarget) {
      handleMobileSearchClose();
    }
  }, [handleMobileSearchClose]);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <AppBar
        elevation={trigger ? 4 : 0}
        sx={!trigger ? {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: theme.palette.divider,
        } : {}}
      >
        <Toolbar>
          {/* Mobile Search Overlay */}
          <Fade in={isMobileSearchExpanded}>
            <MobileSearchContainer 
              expanded={isMobileSearchExpanded}
              onClick={handleOverlayClick}
            >
              <Box 
                sx={{ 
                  width: '100%', 
                  px: 2,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <SearchBar 
                  onSearchClose={handleMobileSearchClose} 
                  isMobileExpanded={isMobileSearchExpanded}
                />
              </Box>
            </MobileSearchContainer>
          </Fade>

          <IconButton
            edge="start"
            sx={{
              [theme.breakpoints.up('md')]: {
                marginRight: 1,
              },
            }}
            aria-label="open drawer"
            onClick={() => settings.setNavDrawer(!settings.navDrawerOpen)}
          >
            <MenuIcon htmlColor={theme.custom?.palette?.iconColor} />
          </IconButton>

          {!isMobileSearchExpanded && <LogoContainer />}

          {!isMobile && (
            <Box sx={{ 
              flexGrow: 1,
              ml: 1,
              [theme.breakpoints.up('md')]: {
                flexGrow: 0,
                width: (theme) => theme.spacing(90),
                ml: 9,
              },
            }}>
              <SearchBar onSearchClose={() => {}} />
            </Box>
          )}
          
          {isMobile && !isMobileSearchExpanded && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              flexGrow: 1,
              position: 'relative',
            }}>
              <IconButton
                aria-label="search"
                onClick={handleMobileSearchToggle}
                sx={{
                  backgroundColor: theme.palette.background.highlight,
                  '&:hover': {
                    backgroundColor: alpha(
                      theme.palette.mode === 'dark' 
                        ? theme.palette.secondary.main 
                        : theme.palette.secondary.light,
                      0.2
                    ),
                  },
                }}
              >
                <SearchIcon htmlColor={theme.custom?.palette?.iconColor} />
              </IconButton>
            </Box>
          )}
          
          {!isMobile && <GrowBox />}

          <Fade in={!isMobileSearchExpanded}>
            <Box sx={{ 
              display: 'flex',
              opacity: isMobileSearchExpanded ? 0 : 1,
              transition: theme.transitions.create('opacity', {
                duration: theme.transitions.duration.shorter,
              })
            }}>
              {(!isMobile || !isMobileSearchExpanded) && (
                <>
                  <Box>
                    <IconButton
                      aria-label="toggle dark theme"
                      aria-controls={menuId}
                      onClick={() => settings.setMode(!settings.isDarkMode)}
                      sx={{ ml: isMobile ? 0 : 0 }}
                    >
                      {settings.isDarkMode ? (
                        <ToggleLightModeIcon
                          htmlColor={theme.custom?.palette?.iconColor}
                        />
                      ) : (
                        <ToggleDarkModeIcon
                          htmlColor={theme.custom?.palette?.iconColor}
                        />
                      )}
                    </IconButton>
                  </Box>

                  <Box>
                    <IconButton
                      edge="end"
                      ref={cartRef}
                      aria-label="cart"
                      aria-controls={menuId}
                      aria-haspopup="true"
                      onClick={() => setCartPopoverOpen(true)}
                      sx={{ ml: 1 }}
                    >
                      <Badge
                        badgeContent={cart.getTotalCount()}
                        max={99}
                        color="secondary"
                        invisible={cart.items.length < 1}
                      >
                        <LocalMallTwoToneIcon
                          htmlColor={
                            cart.items.length > 0
                              ? theme.custom?.palette?.iconColorSecondary
                              : theme.custom?.palette?.iconColor
                          }
                        />
                      </Badge>
                    </IconButton>
                  </Box>

                  <Box>
                    <IconButton
                      edge="end"
                      ref={profileMenuRef}
                      aria-label="account of current user"
                      aria-controls={menuId}
                      aria-haspopup="true"
                      onClick={() => setProfilePopoverOpen(true)}
                      sx={{ ml: 1 }}
                    >
                      {auth.isLoggedIn ? (
                        <AccountsIcon htmlColor={theme.custom?.palette?.iconColor} />
                      ) : (
                        <LoginIcon htmlColor={theme.custom?.palette?.iconColor} />
                      )}
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          </Fade>
        </Toolbar>
        <ProfilePopover
          anchorEl={profileMenuRef.current}
          isOpen={isProfilePopoverOpen}
          onClose={() => setProfilePopoverOpen(false)}
        />
        <CartPopover
          anchorEl={cartRef.current}
          isOpen={isCartPopoverOpen}
          onClose={() => setCartPopoverOpen(false)}
        />
      </AppBar>
    </Box>
  );
};

function LogoContainer() {
  const navigate = useNavigate();
  const settings = useContext(SettingsContext);
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'stretch',
        cursor: 'pointer',
      }}
      onClick={() => navigate(ROOT_URL)}
    >
      <LogoImage src={settings.isDarkMode ? logoDark : logo} alt={'logo'} />
    </Box>
  );
}

export default Appbar;
